import { ChartLayoutManager, ViewMode } from './ChartLayoutManager'
import { useScreenerHistory } from '@/hooks/useScreenerHistory'
import { rawHistoryDetailLabel, SCREENER_INTERVAL_SECONDS } from '@/lib/screenerHistory'
import { ChartTimeContext } from './ChartTimeNavigation'
import type { ScreenerTickerData, ScreenerTimeframe } from '@/types'
import type { ChartAlertControls } from './ChartLevelAlerts'
import type { ChartLayoutPreferenceControls } from '@/hooks/useChartLayoutPreferences'
import type { ScreenerMetric } from '@/lib/api'
import { canonicalExchangeScope, canonicalExchangeName } from '@/lib/screenerPriceExchange'

interface ChartGridProps {
  tickerData: ScreenerTickerData | null
  symbol: string
  mode: ViewMode
  selectedExchanges: string[]
  priceExchange: string
  interval: string
  orderStorageKey: string
  alertControls?: ChartAlertControls
  timeframe?: ScreenerTimeframe
  onGoToLatest?: () => void
  layoutPreferences?: ChartLayoutPreferenceControls
  includeOIOhlc?: boolean
  selectedMarkets?: string[]
  metrics?: ScreenerMetric[]
  onMetricsChange?: (metrics: ScreenerMetric[]) => void
  initialLoading?: boolean
  initialError?: string | null
  onRetryInitial?: () => void
}

export function ChartGrid(props: ChartGridProps) {
  return <HistoryChartGrid {...props} />
}

function HistoryChartGrid({
  tickerData,
  symbol,
  mode,
  selectedExchanges,
  priceExchange,
  interval,
  orderStorageKey,
  alertControls,
  timeframe,
  onGoToLatest,
  layoutPreferences,
  includeOIOhlc = false,
  selectedMarkets,
  metrics,
  onMetricsChange,
  initialLoading,
  initialError,
  onRetryInitial,
}: ChartGridProps) {
  // The price venue is independent of the visible OI venues. Historical reads
  // must retain it, otherwise browsing silently changes the price chart source.
  const visibleExchanges = canonicalExchangeScope(selectedExchanges)
  const historyExchanges = canonicalExchangeScope([...visibleExchanges, priceExchange])
  const history = useScreenerHistory(symbol, tickerData, interval, timeframe, historyExchanges, includeOIOhlc, selectedMarkets, metrics,
    initialLoading === undefined ? undefined : { loading: initialLoading, error: initialError || null })
  const effectiveInterval = history.data?.oi_interval || interval
  const step = SCREENER_INTERVAL_SECONDS[effectiveInterval] || 60
  const narrowRange = history.navigation.range.to - history.navigation.range.from < step * 2
  const showNearby = () => {
    const center = Math.floor((history.navigation.range.from + history.navigation.range.to) / 2 / step) * step
    history.navigation.navigate({ from: center - step, to: center + step * 2 })
  }
  return (
    <ChartTimeContext.Provider value={history.navigation}>
      <div className="mb-2 flex min-h-7 flex-wrap items-center gap-3 px-1 text-xs text-slate-400" aria-live="polite">
        <span>{history.browsing ? 'Fixed view · data updates continue' : history.centered ? 'Live · centered' : 'Live'} · Drag to browse · Scroll to zoom</span>
        {interval === '5s' && <span>{rawHistoryDetailLabel(timeframe, history.navigation.range, history.browsing)}.
          {' '}Showing {new Date(history.navigation.range.from * 1000).toLocaleString()} – {new Date(history.navigation.range.to * 1000).toLocaleString()}.
          {' '}Drag for earlier data; choose a larger interval for the full selected range.</span>}
        {effectiveInterval !== interval && <span>Showing {effectiveInterval} · Older or wider ranges use retained resolution</span>}
        {narrowRange && <button className="underline text-teal-300" onClick={showNearby}>Show nearby {effectiveInterval} samples</button>}
        {history.data?.history_window && Object.values(history.data.exchanges).every(data => !data.oi_history?.length)
          && <span>No stored samples in this window.</span>}
        {history.loading && <span role="status">Loading history…</span>}
        {history.data?.oi_history_load?.status === 'loading' && <span role="status">Loading older OI history…</span>}
        {history.showingPrevious && <span>Showing the previous loaded range.</span>}
        {history.error && <span role="alert" className="text-amber-400">{history.error} <button className="underline"
          onClick={!tickerData && initialError && onRetryInitial ? onRetryInitial : history.retry}>Retry</button></span>}
        {history.browsing && <button type="button" className="rounded border border-slate-600 px-2 py-1 text-teal-300" onClick={() => { history.goLive(); onGoToLatest?.() }}>Go to latest</button>}
        <button type="button" className="rounded border border-slate-600 px-2 py-1 text-teal-300"
          onClick={() => { history.centerCurrent(); if (timeframe?.mode === 'absolute') onGoToLatest?.() }}>Center current</button>
      </div>
      <ChartLayoutManager
        oiLineMode={!includeOIOhlc}
        tickerData={history.data}
        symbol={symbol}
        mode={mode}
        selectedExchanges={visibleExchanges}
        priceExchange={canonicalExchangeName(priceExchange)}
        interval={effectiveInterval}
        orderStorageKey={orderStorageKey}
        alertControls={alertControls}
        layoutPreferences={layoutPreferences}
        onMetricsChange={onMetricsChange}
      />
    </ChartTimeContext.Provider>
  )
}
